import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Search, Eye, ClipboardList, Download, XCircle, Archive, ArrowUpDown, ArrowUp, ArrowDown } from "lucide-react";
import SLABadge from "../components/SLABadge";
import api, { API_URL } from "../lib/api";
import { toast } from "sonner";

const STATUS_COLORS = {
  NEW: "bg-slate-50 text-slate-600 border-slate-200",
  AGENT: "bg-blue-50 text-blue-700 border-blue-200",
  SCHEDULED: "bg-purple-50 text-purple-700 border-purple-200",
  IN_TRANSIT: "bg-amber-50 text-amber-700 border-amber-200",
  JHB_DEPOT: "bg-cyan-50 text-cyan-700 border-cyan-200",
  DELIVERED: "bg-emerald-50 text-emerald-700 border-emerald-200",
  COMPLETED: "bg-slate-100 text-slate-500 border-slate-200",
  CANCELLED: "bg-red-50 text-red-700 border-red-200",
};

function formatDate(d) {
  if (!d) return "-";
  return new Date(d).toLocaleDateString("en-ZA", { day: "2-digit", month: "short", year: "numeric" });
}

export default function JobList() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [total, setTotal] = useState(0);
  const [sortBy, setSortBy] = useState(null); // null = default, "status", "sla", "created"
  const [sortDir, setSortDir] = useState("asc"); // "asc" or "desc"
  const [companies, setCompanies] = useState([]);
  const [companyId, setCompanyId] = useState(() => {
    try { return localStorage.getItem("cargodash_jobs_company") || "all"; } catch { return "all"; }
  });
  const [coverage, setCoverage] = useState(() => {
    try { return localStorage.getItem("cargodash_jobs_coverage") || "all"; } catch { return "all"; }
  });
  const [regions, setRegions] = useState([]);
  const [region, setRegion] = useState(() => {
    try { return localStorage.getItem("cargodash_jobs_region") || "all"; } catch { return "all"; }
  });
  const [areaRegionMap, setAreaRegionMap] = useState({}); // area_name -> region for the column display

  useEffect(() => {
    try { localStorage.setItem("cargodash_jobs_company", companyId); } catch { /* no-op */ }
  }, [companyId]);

  useEffect(() => {
    try { localStorage.setItem("cargodash_jobs_coverage", coverage); } catch { /* no-op */ }
  }, [coverage]);

  useEffect(() => {
    try { localStorage.setItem("cargodash_jobs_region", region); } catch { /* no-op */ }
  }, [region]);

  // Managers / staff can see & narrow by company (3PA is server-side hard-scoped)
  const canPickCompany = user?.role && ["CM", "SM", "SA", "CI", "CS"].includes(user.role);

  useEffect(() => {
    if (!canPickCompany) return;
    api.get("/companies").then(({ data }) => setCompanies(data || [])).catch(() => { /* ignore 403 */ });
  }, [canPickCompany]);

  useEffect(() => {
    Promise.all([api.get("/regions"), api.get("/areas")])
      .then(([reg, ar]) => {
        setRegions((reg.data || []).map((r) => (typeof r === "string" ? r : r.name)));
        const map = {};
        (ar.data || []).forEach((a) => { if (a.area_name) map[a.area_name] = a.region || ""; });
        setAreaRegionMap(map);
      })
      .catch(() => { /* ignore */ });
  }, []);

  useEffect(() => { loadJobs(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [statusFilter, companyId, coverage, region]);

  useEffect(() => {
    const interval = setInterval(loadJobs, 60000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, companyId, coverage, region]);

  const loadJobs = async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter && statusFilter !== "all") params.status = statusFilter;
      if (canPickCompany && companyId && companyId !== "all") params.company_id = companyId;
      if (coverage && coverage !== "all") params.coverage = coverage;
      if (region && region !== "all") params.region = region;
      const { data } = await api.get("/job-cards", { params });
      setJobs(data.items || []);
      setTotal(data.total || 0);
    } catch { toast.error("Failed to load jobs"); }
    setLoading(false);
  };

  const filtered = search
    ? jobs.filter(
        (j) =>
          j.job_card_number?.toLowerCase().includes(search.toLowerCase()) ||
          j.model?.toLowerCase().includes(search.toLowerCase()) ||
          j.store?.toLowerCase().includes(search.toLowerCase()) ||
          j.area?.toLowerCase().includes(search.toLowerCase())
      )
    : jobs;

  const STATUS_ORDER = ["NEW","AGENT","SCHEDULED","IN_TRANSIT","JHB_DEPOT","DELIVERED","COMPLETED","CANCELLED"];
  const SLA_ORDER = { late: 0, warning: 1, on_track: 2, completed: 3 };

  const sorted = sortBy ? [...filtered].sort((a, b) => {
    let cmp = 0;
    if (sortBy === "status") {
      cmp = STATUS_ORDER.indexOf(a.status) - STATUS_ORDER.indexOf(b.status);
    } else if (sortBy === "sla") {
      cmp = (SLA_ORDER[a.sla?.status] ?? 9) - (SLA_ORDER[b.sla?.status] ?? 9);
    } else if (sortBy === "created") {
      cmp = (a.date_created || "").localeCompare(b.date_created || "");
    }
    return sortDir === "desc" ? -cmp : cmp;
  }) : filtered;

  const toggleSort = (col) => {
    if (sortBy === col) {
      if (sortDir === "asc") setSortDir("desc");
      else { setSortBy(null); setSortDir("asc"); } // third click resets
    } else {
      setSortBy(col);
      setSortDir("asc");
    }
  };

  const sortIcon = (col) => {
    if (sortBy !== col) return <ArrowUpDown size={12} className="text-slate-300" />;
    return sortDir === "asc"
      ? <ArrowUp size={12} className="text-teal-600" />
      : <ArrowDown size={12} className="text-teal-600" />;
  };

  const exportCSV = async () => {
    try {
      const token = localStorage.getItem("token");
      const params = new URLSearchParams();
      if (statusFilter && statusFilter !== "all") params.append("status", statusFilter);
      const url = `${API_URL}/api/job-cards/export/csv?${params.toString()}`;
      const response = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      const blob = await response.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = "job_cards_export.csv";
      a.click();
      URL.revokeObjectURL(a.href);
      toast.success("CSV exported");
    } catch {
      toast.error("Export failed");
    }
  };

  const isCargoDash = ["CI", "CS", "CM"].includes(user?.role);

  const cancelJob = async (jobId) => {
    if (!window.confirm("Are you sure you want to cancel this job card?")) return;
    try {
      await api.put(`/job-cards/${jobId}/cancel`);
      toast.success("Job card cancelled");
      loadJobs();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to cancel");
    }
  };

  const archiveJob = async (jobId) => {
    try {
      await api.put(`/job-cards/${jobId}/archive`);
      toast.success("Job card archived");
      loadJobs();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to archive");
    }
  };

  return (
    <div data-testid="job-list-page" className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading font-bold text-2xl tracking-tight text-slate-900 flex items-center gap-2">
            <ClipboardList size={24} className="text-teal-600" /> Job Cards
          </h1>
          <p className="text-slate-500 text-sm mt-1">{total} total job cards</p>
        </div>
        {["CM", "SM", "CI"].includes(user?.role) && (
          <Button
            data-testid="export-csv-btn"
            onClick={exportCSV}
            variant="outline"
            className="border-slate-300 text-slate-600 hover:bg-slate-50"
          >
            <Download size={14} className="mr-2" /> Export CSV
          </Button>
        )}
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            data-testid="job-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by JC#, model, store..."
            className="bg-white border-slate-200 pl-9 text-sm"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger data-testid="status-filter" className="w-40 bg-white border-slate-200">
            <SelectValue placeholder="All Statuses" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="NEW">New</SelectItem>
            <SelectItem value="AGENT">Agent</SelectItem>
            <SelectItem value="SCHEDULED">Scheduled</SelectItem>
            <SelectItem value="IN_TRANSIT">In Transit</SelectItem>
            <SelectItem value="JHB_DEPOT">JHB Depot</SelectItem>
            <SelectItem value="DELIVERED">Delivered</SelectItem>
            <SelectItem value="COMPLETED">Completed</SelectItem>
            <SelectItem value="CANCELLED">Cancelled</SelectItem>
          </SelectContent>
        </Select>
        {canPickCompany && companies.length > 0 && (
          <Select value={companyId} onValueChange={setCompanyId}>
            <SelectTrigger data-testid="jobs-company-filter" className="w-52 bg-white border-slate-200">
              <SelectValue placeholder="All companies" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All companies</SelectItem>
              {companies.map((c) => (
                <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        <Select value={coverage} onValueChange={setCoverage}>
          <SelectTrigger data-testid="jobs-coverage-filter" className="w-36 bg-white border-slate-200">
            <SelectValue placeholder="Any coverage" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Any coverage</SelectItem>
            <SelectItem value="LOCAL">Local</SelectItem>
            <SelectItem value="NATIONAL">National</SelectItem>
          </SelectContent>
        </Select>
        <Select value={region} onValueChange={setRegion}>
          <SelectTrigger data-testid="jobs-region-filter" className="w-44 bg-white border-slate-200">
            <SelectValue placeholder="Any region" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Any region</SelectItem>
            {regions.map((r) => (<SelectItem key={r} value={r}>{r}</SelectItem>))}
          </SelectContent>
        </Select>
        <Button variant="outline" size="sm" onClick={loadJobs} className="border-slate-200 text-slate-500">
          Refresh
        </Button>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-slate-200 text-slate-400 text-[10px] uppercase tracking-wider">
              <th className="text-left p-3">Job Card #</th>
              <th className="text-left p-3">
                <button onClick={() => toggleSort("status")} className="flex items-center gap-1 hover:text-slate-600" data-testid="sort-status">
                  Status {sortIcon("status")}
                </button>
              </th>
              <th className="text-left p-3">
                <button onClick={() => toggleSort("sla")} className="flex items-center gap-1 hover:text-slate-600" data-testid="sort-sla">
                  SLA {sortIcon("sla")}
                </button>
              </th>
              <th className="text-left p-3">Model</th>
              <th className="text-left p-3">Store</th>
              <th className="text-left p-3">Area</th>
              <th className="text-left p-3">Region</th>
              <th className="text-left p-3">Coverage</th>
              <th className="text-left p-3">Type</th>
              <th className="text-left p-3">
                <button onClick={() => toggleSort("created")} className="flex items-center gap-1 hover:text-slate-600" data-testid="sort-created">
                  Created {sortIcon("created")}
                </button>
              </th>
              <th className="text-right p-3">Action</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={10} className="p-8 text-center text-slate-400">Loading...</td></tr>
            )}
            {!loading && sorted.length === 0 && (
              <tr>
                <td colSpan={10} className="p-12 text-center">
                  <ClipboardList size={32} className="mx-auto text-slate-400 mb-2" />
                  <p className="text-slate-400">No job cards found</p>
                </td>
              </tr>
            )}
            {!loading && sorted.map((jc) => (
              <tr key={jc.id} className="border-b border-slate-200 hover:bg-slate-50 transition-colors">
                <td className="p-3">
                  <span className="font-medium text-slate-800">{jc.job_card_number}</span>
                </td>
                <td className="p-3">
                  <Badge variant="outline" className={`status-badge ${STATUS_COLORS[jc.status] || ""}`}>
                    {jc.status}
                  </Badge>
                </td>
                <td className="p-3"><SLABadge sla={jc.sla} showDays={false} /></td>
                <td className="p-3 text-slate-500">{jc.model || "-"}</td>
                <td className="p-3 text-slate-500">{jc.store || "-"}</td>
                <td className="p-3 text-slate-500">{jc.area || "-"}</td>
                <td className="p-3">
                  {areaRegionMap[jc.area] ? (
                    <span className="inline-block px-2 py-0.5 rounded-full text-[10px] font-medium border bg-amber-50 text-amber-700 border-amber-200">
                      {areaRegionMap[jc.area]}
                    </span>
                  ) : <span className="text-slate-300 text-xs">-</span>}
                </td>
                <td className="p-3">
                  {jc.coverage ? (
                    <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-medium border ${
                      jc.coverage === "LOCAL"
                        ? "bg-sky-50 text-sky-700 border-sky-200"
                        : "bg-violet-50 text-violet-700 border-violet-200"
                    }`}>{jc.coverage}</span>
                  ) : <span className="text-slate-300 text-xs">-</span>}
                </td>
                <td className="p-3">
                  {jc.job_type ? (
                    <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-medium ${
                      jc.job_type === "DELIVERY"
                        ? "bg-blue-50 text-blue-700 border border-blue-200"
                        : "bg-purple-50 text-purple-700 border border-purple-200"
                    }`}>{jc.job_type}</span>
                  ) : <span className="text-slate-300 text-xs">-</span>}
                </td>
                <td className="p-3 text-slate-400 text-xs">{formatDate(jc.date_created)}</td>
                <td className="p-3 text-right">
                  <div className="flex items-center justify-end gap-1">
                    <Button
                      data-testid={`view-job-${jc.id}`}
                      variant="ghost"
                      size="sm"
                      onClick={() => navigate(`/jobs/${jc.id}`)}
                      className="text-teal-600 hover:text-teal-700 text-xs"
                    >
                      <Eye size={14} className="mr-1" /> View
                    </Button>
                    {isCargoDash && !["COMPLETED", "CANCELLED"].includes(jc.status) && (
                      <Button
                        data-testid={`cancel-job-${jc.id}`}
                        variant="ghost"
                        size="sm"
                        onClick={() => cancelJob(jc.id)}
                        className="text-red-400 hover:text-red-600 text-xs"
                      >
                        <XCircle size={14} className="mr-1" /> Cancel
                      </Button>
                    )}
                    {isCargoDash && ["COMPLETED", "CANCELLED"].includes(jc.status) && (
                      <Button
                        data-testid={`archive-job-${jc.id}`}
                        variant="ghost"
                        size="sm"
                        onClick={() => archiveJob(jc.id)}
                        className="text-slate-400 hover:text-slate-600 text-xs"
                      >
                        <Archive size={14} className="mr-1" /> Archive
                      </Button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
